'use client';

import { PortalSimple } from '@components/portal-simple/PortalSimple';
import ROUTES from '@lib/constants/routes';
import { fetchSlider, sliderSelector } from '@lib/store/slices/NewsSlice';
import extractSliderName from '@lib/tools/_extract_slider_name';
import { handleBodyLock } from '@lib/tools/_handle-body-lock';
import { handleBodyUnlock } from '@lib/tools/_handle-body-unlock';
import { SCHEMA, URLS } from '@lib/tools/helpers/constants';
import { SafeHtmlRenderer } from '@lib/tools/helpers/SafeHtmlRenderer';
import { useOnClickOutside } from '@lib/tools/hooks/useOnClickOutside';
import cn from 'classnames';
import gsap from 'gsap';
import KeenSlider from 'keen-slider';
import Head from 'next/head';
import NextImage from 'next/image';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import style from './ModalContent.module.scss';
function updateClasses(slider, arrowLeft, dots, arrowRight, nums) {
  const slide = slider.track.details.rel;
  const currSlideString = slider.track.details.rel + 1 > 9 ? slider.track.details.rel + 1 : `0${slider.track.details.rel + 1}`;
  const slideArrLength = slider.track.details.slides.length > 9 ? slider.track.details.slides.length : `0${slider.track.details.slides.length}`;
  slide === 0 ? arrowLeft.classList.add('arrow--disabled') : arrowLeft.classList.remove('arrow--disabled');
  slide === slider.track.details.slides.length - 1 ? arrowRight.classList.add('arrow--disabled') : arrowRight.classList.remove('arrow--disabled');
  Array.from(dots.children).forEach((dot, idx) => {
    idx === slide ? dot.classList.add('dot--active') : dot.classList.remove('dot--active');
  });
  nums.innerHTML = `${currSlideString} / ${slideArrLength}`;
}
export const ModalContent = ({
  item,
  handleCloseModal,
  sliderData,
  type = 'Новость',
  isSchema
}) => {
  const [keenSlider, setKeenSlider] = useState(null);
  const [sliderName, setSliderName] = useState(null);
  const [portalMounted, setPortalMounted] = useState(false);
  const el = useRef();
  const ref = useRef();
  const isClosingRef = useRef(false);
  const dispatch = useDispatch();
  const sliderInfo = useSelector(sliderSelector);
  const itemContent = item.content;
  const sourceImage = item.imagePreview;
  useOnClickOutside(ref, () => {
    handleClose();
  });
  const handleClose = () => {
    isClosingRef.current = true;
    gsap.to(ref.current, {
      transform: 'translateX(100vw)',
      duration: 0.4,
      ease: 'easeOut',
      onComplete: () => {
        handleBodyUnlock();
        handleCloseModal();
      }
    });
    gsap.to(el.current, {
      background: 'rgba(28, 28, 28, 0)',
      duration: 0.4,
      ease: 'easeOut'
    });
  };
  const createHTML = (sliderName_0, itemContent_0) => {
    const regex = /{{([^{}]*)}}/g;
    if (sliderName_0 && sliderData) {
      const imageArr = sliderData.absolutePath;
      return <div className='news_modal_content__inner' itemProp={isSchema ? 'articleBody' : undefined}>
          <SafeHtmlRenderer tag='div' content={itemContent_0.split(regex)[0] || ''} />
          <div>
            <div className='keen-slider' id='my-keen-slider'>
              {imageArr.map(item_0 => {
              return <div key={item_0} className='keen-slider__slide'>
                    <NextImage fill alt='promo-slide' src={item_0.replace(' ', '%20')} />
                  </div>;
            })}
            </div>
          </div>
          <SafeHtmlRenderer tag='div' content={itemContent_0.split(regex)[2] || ''} />
        </div>;
    }
    return <SafeHtmlRenderer tag='div' className='news_modal_content__inner' itemProp={isSchema ? 'articleBody' : undefined} content={itemContent_0 || ''} />;
  };
  function navigation(slider) {
    let wrapper, dots, arrowLeft, arrowRight, nums;
    function markup(remove) {
      wrapperMarkup(remove);
      dotMarkup(remove);
      arrowMarkup(remove);
      numsMarkup();
    }
    function removeElement(elment) {
      elment.remove();
    }
    function createDiv(className) {
      const div = document.createElement('div');
      const classNames = className.split(' ');
      classNames.forEach(name => div.classList.add(name));
      return div;
    }
    function arrowMarkup(remove_0) {
      if (remove_0) {
        removeElement(arrowLeft);
        removeElement(arrowRight);
        return;
      }
      arrowLeft = createDiv('arrow arrow--left');
      arrowLeft.addEventListener('click', () => slider.prev());
      arrowRight = createDiv('arrow arrow--right');
      arrowRight.addEventListener('click', () => slider.next());
      wrapper.append(arrowLeft);
      wrapper.append(arrowRight);
    }
    function wrapperMarkup(remove_1) {
      if (remove_1) {
        const parent = wrapper.parentNode;
        while (wrapper.firstChild) parent.insertBefore(wrapper.firstChild, wrapper);
        removeElement(wrapper);
        return;
      }
      wrapper = createDiv('navigation-wrapper');
      slider.container.parentNode.append(wrapper);
      wrapper.append(slider.container);
    }
    function dotMarkup(remove_2) {
      if (remove_2) {
        removeElement(dots);
        return;
      }
      dots = createDiv('dots');
      slider.track.details.slides.forEach((_e, idx) => {
        const dot = createDiv('dot');
        dot.addEventListener('click', () => slider.moveToIdx(idx));
        dots.append(dot);
      });
      wrapper.append(dots);
    }
    function numsMarkup() {
      nums = createDiv('nums');
      wrapper.append(nums);
    }
    slider.on('created', () => {
      markup();
      updateClasses(slider, arrowLeft, dots, arrowRight, nums);
    });
    slider.on('optionsChanged', () => {
      markup(true);
      markup();
      updateClasses(slider, arrowLeft, dots, arrowRight, nums);
    });
    slider.on('slideChanged', () => {
      updateClasses(slider, arrowLeft, dots, arrowRight, nums);
    });
    slider.on('destroyed', () => {
      markup(true);
    });
  }
  useLayoutEffect(() => {
    let rafId = 0;
    let cancelled = false;
    const run = () => {
      if (cancelled) return;
      const panel = ref.current;
      const overlay = el.current;
      if (!panel || !overlay) {
        rafId = requestAnimationFrame(run);
        return;
      }
      handleBodyLock();
      gsap.fromTo(panel, {
        transform: 'translateX(100vw)'
      }, {
        transform: 'translateX(0px)',
        duration: 0.4,
        ease: 'easeOut'
      });
      gsap.fromTo(overlay, {
        background: 'rgba(28, 28, 28, 0)'
      }, {
        background: 'rgba(28, 28, 28, 0.25)',
        duration: 0.4,
        ease: 'easeOut'
      });
    };
    run();
    return function () {
      cancelled = true;
      cancelAnimationFrame(rafId);
      if (!isClosingRef.current) {
        handleBodyUnlock();
      }
    };
  }, []);
  useEffect(() => {
    if (window && document.querySelector('#my-keen-slider')) {
      const slider_0 = new KeenSlider('#my-keen-slider', {
        slides: {
          perView: 0.985
        }
      }, [navigation]);
      if (!keenSlider) setKeenSlider(slider_0);
    }
  }, [sliderName, sliderInfo]);
  useEffect(() => {
    if (extractSliderName(itemContent)) {
      setSliderName(extractSliderName(itemContent));
    }
  }, [item, itemContent]);
  useEffect(() => {
    if (sliderName) {
      dispatch(fetchSlider(sliderName));
    }
  }, [dispatch, sliderName]);
  useEffect(() => setPortalMounted(true), []);
  return <>
      {!portalMounted && item.title && <h1 className='titleHidden'>{item.title}</h1>}

      <PortalSimple>
        {item.title && <Head>
            <meta property='og:title' content={item.title} />
            <link rel='canonical' href={`${URLS.MOMENTS_PROD}/${item?.slug}`} />
            <title>{item.title} | Новости</title>
            <meta name='description' content={`${item.title}. Читайте актуальные новости ЖК Моментс от FORMA на официальном сайте!`} />
          </Head>}

        <div className={cn(style.content, 'news_modal_content')} ref={el} itemScope={isSchema} itemType={isSchema ? SCHEMA.ARTICLE : undefined}>
          <div className={style.container} ref={ref}>
            <div className={style.inner}>
              <p className={style.name}>{type}</p>
              <h1 className={style.title} itemProp={isSchema ? 'headline' : undefined}>
                {item.title}
              </h1>

              <span className='news_modal_img_wrapper'>
                <NextImage fill className='news_modal_img' src={sourceImage} alt='promo' itemProp={isSchema ? 'image' : undefined} />
              </span>

              {createHTML(sliderName, itemContent)}
            </div>
            {isSchema && <time itemProp='datePublished' dateTime={item.createdAt}></time>}

            <div type='button' onPointerDown={e => {
            e.preventDefault();
            handleClose();
          }}>
              <span className={style.close}>
                <span className={style.line} />
                <span className={style.line} />
              </span>
            </div>
          </div>
          {isSchema && <meta itemScope itemProp='mainEntityOfPage' itemType={SCHEMA.WEB_PAGE} itemID={`${URLS.MOMENTS_PROD}${ROUTES.news.root}/${item?.slug}`} />}
        </div>
      </PortalSimple>
    </>;
};